import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AdminConversationDto,
  AdminPermission,
  AuditLogDto,
  AuditLogEventType,
  BlockDto,
  LikeDto,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import ConversationDetailPopup from '../../components/ConversationDetail'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { usePermissions } from '../../contexts/PermissionsContext'

type InteractionsView = 'likes' | 'blocks' | 'conversations' | 'vibes' | 'intentions'

const USER_INTENTION_AUDIT_EVENT_TYPES = `${AuditLogEventType.SetUserIntention},${AuditLogEventType.ClearUserIntention}`

const VIEW_TABS: Array<{ key: InteractionsView; label: string; permission: AdminPermission }> = [
  { key: 'likes', label: 'Likes', permission: AdminPermission.Likes },
  { key: 'blocks', label: 'Blocks', permission: AdminPermission.Blocks },
  { key: 'conversations', label: 'Conversations', permission: AdminPermission.Conversations },
  { key: 'vibes', label: 'Vibe', permission: AdminPermission.AuditLogs },
  { key: 'intentions', label: 'Intentions', permission: AdminPermission.Intentions },
]

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 220
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

function withRowKey<T>(items: T[]): Array<T & { _key: string }> {
  return items.map(item => ({
    ...item,
    _key:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36),
  }))
}

const UserInteractionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()

  const [likes, setLikes] = useState<Array<LikeDto & { _key: string }>>([])
  const [likesPagination, setLikesPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [blocks, setBlocks] = useState<Array<BlockDto & { _key: string }>>([])
  const [conversations, setConversations] = useState<AdminConversationDto[]>([])
  const [conversationsPagination, setConversationsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [selectedConversationPublicId, setSelectedConversationPublicId] = useState<string | null>(null)
  const [isConversationPopupVisible, setIsConversationPopupVisible] = useState(false)
  const [vibeLogs, setVibeLogs] = useState<AuditLogDto[]>([])
  const [vibeLogsPagination, setVibeLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [intentionLogs, setIntentionLogs] = useState<AuditLogDto[]>([])
  const [intentionLogsPagination, setIntentionLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)
  const isPopupOperation = useRef(false)

  const visibleTabs = useMemo(
    () => (isPermissionsLoading ? [] : VIEW_TABS.filter(tab => hasPermission(tab.permission))),
    [hasPermission, isPermissionsLoading],
  )

  const requestedView = (searchParams.get('view') as InteractionsView | null) ?? 'likes'
  const activeView = visibleTabs.some(tab => tab.key === requestedView)
    ? requestedView
    : (visibleTabs[0]?.key ?? 'likes')

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || (activeView === 'likes' ? 'likedAt' : 'createdAt')
  const currentSortOrder = searchParams.get('sortOrder') || 'desc'
  const period = searchParams.get('period') || ''
  const isLegacyPeriodView = activeView === 'conversations' && period.length > 0

  useEffect(() => {
    if (requestedView !== activeView && visibleTabs.length > 0) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('view', activeView)
      nextParams.set('page', '1')
      setSearchParams(nextParams, { replace: true })
    }
  }, [activeView, requestedView, searchParams, setSearchParams, visibleTabs.length])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (activeView !== 'likes') {
      return
    }

    const fetchLikes = async () => {
      try {
        const response = await adminService.getLikes(
          currentPage,
          currentPageSize,
          '',
          currentSortBy,
          currentSortOrder,
        )
        setLikes(withRowKey(response.likes))
        setLikesPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch likes', err)
      }
    }

    fetchLikes()
  }, [activeView, currentPage, currentPageSize, currentSortBy, currentSortOrder])

  useEffect(() => {
    if (activeView !== 'blocks') {
      return
    }

    const fetchBlocks = async () => {
      try {
        const response = await adminService.getBlocks()
        setBlocks(withRowKey(response))
      } catch (err) {
        console.log('Failed to fetch blocks', err)
      }
    }

    fetchBlocks()
  }, [activeView])

  useEffect(() => {
    if (activeView !== 'vibes') {
      return
    }

    const fetchVibes = async () => {
      try {
        const response = await adminService.getAuditLogs(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortOrder as any,
          AuditLogEventType.SetVibeEmoji,
        )

        const normalized = response.auditLogs.map((log) => {
          const anyLog = log as any
          const data = anyLog?.data
          if (typeof data === 'string') {
            try {
              return { ...anyLog, data: JSON.parse(data) }
            } catch {
              return anyLog
            }
          }
          return anyLog
        }) as AuditLogDto[]

        setVibeLogs(normalized)
        setVibeLogsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch vibe logs', err)
      }
    }

    fetchVibes()
  }, [activeView, currentPage, currentPageSize, currentSortOrder, debouncedSearchValue])

  useEffect(() => {
    if (activeView !== 'intentions') {
      return
    }

    const fetchIntentions = async () => {
      try {
        const response = await adminService.getAuditLogs(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortOrder as any,
          USER_INTENTION_AUDIT_EVENT_TYPES,
        )

        const normalized = response.auditLogs.map((log) => {
          const anyLog = log as any
          const data = anyLog?.data
          if (typeof data === 'string') {
            try {
              return { ...anyLog, data: JSON.parse(data) }
            } catch {
              return anyLog
            }
          }
          return anyLog
        }) as AuditLogDto[]

        setIntentionLogs(normalized)
        setIntentionLogsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch intention logs', err)
      }
    }

    fetchIntentions()
  }, [activeView, currentPage, currentPageSize, currentSortOrder, debouncedSearchValue])

  const fetchConversations = useCallback(async () => {
    if (activeView !== 'conversations' || isPopupOperation.current) {
      return
    }

    try {
      if (isLegacyPeriodView) {
        const legacy = await adminService.getConversations(period)
        setConversations(
          legacy.map(c => ({
            id: c.id,
            publicId: c.publicId,
            fromUser: c.fromUser,
            toUser: c.toUser,
            messages: [],
            duration: c.duration,
            createdAt: c.createdAt,
          })),
        )
        return
      }

      const response = await adminService.getConversationsPaginated(
        currentPage,
        currentPageSize,
        debouncedSearchValue,
        currentSortBy,
        currentSortOrder,
      )
      setConversations(response.conversations)
      setConversationsPagination(response.pagination)

      const popupId = searchParams.get('popup')
      if (popupId) {
        setSelectedConversationPublicId(popupId)
      }
    } catch (err) {
      console.log('Failed to fetch conversations', err)
    }
  }, [
    activeView,
    currentPage,
    currentPageSize,
    debouncedSearchValue,
    currentSortBy,
    currentSortOrder,
    isLegacyPeriodView,
    period,
    searchParams,
  ])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  useEffect(() => {
    if (selectedConversationPublicId) {
      isPopupOperation.current = true
      setIsConversationPopupVisible(true)
    }
  }, [selectedConversationPublicId])

  const handleViewChange = (view: InteractionsView) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', view)
    nextParams.set('page', '1')
    if (view !== 'conversations') {
      nextParams.delete('period')
      nextParams.delete('popup')
    }
    setSearchParams(nextParams)
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleConversationRowClick = (row: AdminConversationDto) => {
    setSelectedConversationPublicId(row.publicId)
  }

  const handleCloseConversationPopup = () => {
    setIsConversationPopupVisible(false)
    isPopupOperation.current = true
    setTimeout(() => {
      setSelectedConversationPublicId(null)
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('popup')
      navigate(`?${newParams.toString()}`, { replace: true })
      isPopupOperation.current = false
    }, 300)
  }

  const pageTitle =
    isLegacyPeriodView && period === 'last-month'
      ? 'Messages in last month'
      : activeView === 'vibes'
        ? 'User vibe checks'
        : activeView === 'intentions'
          ? 'User intentions'
          : 'User interactions'

  const showPagination =
    (activeView === 'likes' && likesPagination.totalPages > 1) ||
    (activeView === 'conversations' && !isLegacyPeriodView && conversationsPagination.totalPages > 1) ||
    (activeView === 'vibes' && vibeLogsPagination.totalPages > 1) ||
    (activeView === 'intentions' && intentionLogsPagination.totalPages > 1)

  const paginationProps =
    activeView === 'likes'
      ? { currentPage: likesPagination.page, totalPages: likesPagination.totalPages }
      : activeView === 'vibes'
        ? { currentPage: vibeLogsPagination.page, totalPages: vibeLogsPagination.totalPages }
        : activeView === 'intentions'
          ? { currentPage: intentionLogsPagination.page, totalPages: intentionLogsPagination.totalPages }
          : { currentPage: conversationsPagination.page, totalPages: conversationsPagination.totalPages }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>{pageTitle}</h2>
        {showPagination && (
          <Pagination
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {visibleTabs.map(tab => {
            const classNames = ['tab']
            if (tab.key === activeView) {
              classNames.push('active')
            }

            return (
              <button
                key={tab.key}
                type="button"
                className={classNames.join(' ')}
                onClick={() => handleViewChange(tab.key)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          {activeView === 'likes' && (
            <Table
              id="likes"
              keyField="_key"
              name="Likes"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'likingUser.imageUrl', type: 'userAvatar' },
                { label: 'Liking user', accessor: 'likingUser.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'likedUser.imageUrl', type: 'userAvatar' },
                { label: 'Liked user', accessor: 'likedUser.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Liked at', accessor: 'likedAt', type: 'datetime' },
              ]}
              sourceData={likes}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
            />
          )}

          {activeView === 'blocks' && (
            <Table
              id="blocks"
              name="Blocks"
              keyField="_key"
              initialSortColumn="blockedAt"
              initialSortOrder="desc"
              columns={[
                { label: '', accessor: 'blockingUser.imageUrl', type: 'userAvatar' },
                { label: 'Blocker', accessor: 'blockingUser.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'blockedUser.imageUrl', type: 'userAvatar' },
                { label: 'Blocked', accessor: 'blockedUser.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Blocked at', accessor: 'blockedAt', type: 'datetime' },
                { label: 'Type', accessor: 'reported', type: 'reported' },
              ]}
              sourceData={blocks}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              disableGlobalSearch={true}
            />
          )}

          {activeView === 'conversations' && (
            <Table
              id="conversations"
              name="Conversations"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'fromUser.imageUrl', type: 'userAvatar' },
                { label: 'From', accessor: 'fromUser.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'toUser.imageUrl', type: 'userAvatar' },
                { label: 'To', accessor: 'toUser.lastName', type: 'fullNameAndTelephone' },
                { label: 'Initiated at', accessor: 'createdAt', type: 'datetime' },
                { label: 'Duration', accessor: 'duration', type: 'interval' },
              ]}
              sourceData={conversations}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleConversationRowClick}
              onSortChange={handleSortChange}
              disableGlobalSearch={!isLegacyPeriodView}
              disableClientSort={!isLegacyPeriodView}
            />
          )}

          {activeView === 'vibes' && (
            <Table
              id="vibe-logs"
              name="Vibe checks"
              keyField="publicId"
              initialSortColumn="createdAt"
              initialSortOrder={currentSortOrder}
              columns={[
                { label: 'Set at', accessor: 'createdAt', type: 'datetime' },
                { label: 'User', accessor: 'affectedUserFullName', type: 'text' as any },
                { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                { label: 'Venue', accessor: 'data.venueName', type: 'text' as any },
                { label: 'Comment', accessor: 'comment', type: 'text' as any },
              ]}
              sourceData={vibeLogs}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}

          {activeView === 'intentions' && (
            <Table
              id="intention-logs"
              name="Intentions"
              keyField="publicId"
              initialSortColumn="createdAt"
              initialSortOrder={currentSortOrder}
              columns={[
                { label: 'Set at', accessor: 'createdAt', type: 'datetime' },
                { label: 'User', accessor: 'affectedUserFullName', type: 'text' as any },
                { label: 'Intention', accessor: 'data.name', type: 'text' as any },
                { label: 'Prompt', accessor: 'data.prompt', type: 'text' as any },
                { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                { label: 'Venue', accessor: 'data.venueName', type: 'text' as any },
              ]}
              sourceData={intentionLogs}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}
        </div>
      </div>

      <ConversationDetailPopup
        conversationPublicId={selectedConversationPublicId}
        isOpen={isConversationPopupVisible}
        onClose={handleCloseConversationPopup}
      />
    </div>
  )
}

export default UserInteractionsPage
